import { gql, useQuery } from '@apollo/client';
import { useGrowthBook } from '@growthbook/growthbook-react';
import React, {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import { useAuth } from '~/shared/contexts/AuthContext';

interface Gym {
  id: string;
  name: string;
  logoUrl: string;
  active?: boolean;
}

interface AppDataContextValue {
  loading: boolean;
  gymTimezone: string | null;
  gymName: string | null;
  gymLogoUrl: string | null;
  userRole: string | null;
  userPermissions: string[];
  userEmail: string | null;
  userGyms: Gym[];
  enabledFeatures: {
    classes: boolean;
    facilities: boolean;
    retail: boolean;
    workout: boolean;
    activityLog: boolean;
    customRoles: boolean;
  };
  gymSettings: {
    bypassCheckinConfirmation: boolean;
    trackCheckOuts?: boolean;
  };
}

const AppDataContext = createContext<AppDataContextValue | undefined>(undefined);

const GET_APP_DATA = gql`
  query GetAppData {
    currentGym {
      id
      name
      logoUrl
      timezone
      settings {
        bypassCheckinConfirmation
        trackCheckOuts
      }
    }
    currentUser {
      id
      email
      role
      permissions
      gym {
        id
        logoUrl
        name
        enabledFeatures {
          classes
          facilities
          retail
          workout
          activityLog
          customRoles
        }
      }
      gyms {
        id
        logoUrl
        name
        active
      }
    }
  }
`;

interface AppDataProviderProps {
  children: ReactNode;
}

/**
 * Provider component that fetches and exposes global app data including gym info, user info, and feature flags.
 * Auto-selects the first gym if no gymId is stored. Updates GrowthBook attributes when user data loads.
 */
export const AppDataProvider: FC<AppDataProviderProps> = ({ children }) => {
  const { isReady, accessToken, gymId, setGymId } = useAuth();

  // Allow query without gymId — the API falls back to current_user.gym as tenant.
  // This breaks the circular dependency where gymId can only be set by
  // ResponsiveDrawer, which is blocked from rendering by the loading gate.
  const isSkipped = !isReady || !accessToken;

  const { loading: queryLoading, data } = useQuery(GET_APP_DATA, {
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    skip: isSkipped,
  });

  // Auto-select first gym on fresh sign-in (no stored gymId)
  useEffect(() => {
    const gyms = data?.currentUser?.gyms;
    if (gyms?.length && !gymId) {
      const firstActiveGym = gyms.find((g: Gym) => g.active !== false) || gyms[0];
      setGymId(firstActiveGym.id);
    }
  }, [data, gymId, setGymId]);

  const growthbook = useGrowthBook();

  useEffect(() => {
    if (growthbook && data?.currentUser) {
      growthbook.setAttributes({
        ...growthbook.getAttributes(),
        id: data.currentUser.id,
        email: data.currentUser.email,
        userRole: data.currentUser.role,
      });
    }
  }, [growthbook, data]);

  const loading = isSkipped || queryLoading;

  const value = useMemo<AppDataContextValue>(
    () => ({
      loading,
      gymTimezone: data?.currentGym?.timezone || null,
      gymName: data?.currentGym?.name || null,
      gymLogoUrl: data?.currentGym?.logoUrl || null,
      userRole: data?.currentUser?.role || null,
      userPermissions: data?.currentUser?.permissions || [],
      userEmail: data?.currentUser?.email || null,
      userGyms: data?.currentUser?.gyms || [],
      enabledFeatures: data?.currentUser?.gym?.enabledFeatures || {},
      gymSettings: data?.currentGym?.settings || {},
    }),
    [loading, data]
  );

  return (
    <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>
  );
};

/**
 * Hook to access global app data from AppDataContext.
 * @returns App data including gym info, user info, enabled features, and loading state, or undefined if outside provider
 */
export const useAppData = (): AppDataContextValue | undefined => {
  return useContext(AppDataContext);
};
